import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import {
    selectLayerClickabilityById,
    selectMapLayerIdByEntityName,
} from '@redux/selectors/selectors';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import Button from '@components/buttons/Button';
import { useLayersInteractivity } from '@hooks/map/useLayersInteractivity';
import { publicTransitApi } from '@api/publicTransitApi';
import { getFeatureCenter, getUniqueFeatures } from '@utils/mapUtils';
import { MapboxGeoJSONFeatureWithProperties } from '@redux/map/types';
import { flewToPoint } from '@redux/map/map-reducer';
import LockedLayerNotifier from '@components/LockedLayerNotifier/LockedLayerNotifier';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';
import DialogComponent from '@components/DialogComponent/DialogComponent';
import TransitStopsList from '@features/public-transit-editor/transit-management/TransitStops/ui/TransitStopsList';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { AddFromListIcon } from '@icons/icons/AddFromListIcon';
import Tooltip from '@mui/material/Tooltip';
import './add-stop-to-route.scss';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';

type Props = {
    routeProfileId: string;
    onStopAdditionFn: (reloadLayer?: boolean) => Promise<void>;
};

const AddStopToRoute = memo(({ routeProfileId, onStopAdditionFn }: Props) => {
    const entityName = PublicTransitLayerEntityName.STOPS;
    const routeProfileStopEntityName = PublicTransitLayerEntityName.ROUTE_STOPS;
    const stopsLayerId = useTypedSelector(state => selectMapLayerIdByEntityName(state, entityName));
    const layerClickable = useTypedSelector(state =>
        selectLayerClickabilityById(state, stopsLayerId)
    );
    const [selectionMode, setSelectionMode] = useState<boolean>(false);
    const startedSelectionRef = useRef(false);
    const isSavingRef = useRef(false);
    const { toggleRedirectOnFeatureSelection } = useLayersInteractivity();
    const { recoverLayersInteractivity, disableLayersInteractivityExceptEntityName } =
        useMapLayersClickabilityContext();
    const { mapboxMapId } = useMap();
    const { t } = useTranslation();
    const { addToast } = useToastContext();
    const dispatch = useDispatch();

    const [openDialog, setOpenDialog] = useState(false);

    const handleOpenDialog = useCallback(() => {
        setOpenDialog(prev => !prev);
    }, [setOpenDialog]);

    const handleCloseDialog = useCallback(
        (event, reason?: 'backdropClick' | 'escapeKeyDown') => {
            //don't allow to close dialog by click outside or "escape" btn
            if (!reason) {
                setOpenDialog(false);
            }
        },
        [setOpenDialog]
    );

    const keyPress = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setSelectionMode(false);
            recoverLayersInteractivity();
            toggleRedirectOnFeatureSelection(true);
        }
    };

    useEffect(() => {
        document.addEventListener('keyup', keyPress);

        return () => {
            document.removeEventListener('keyup', keyPress);
            toggleRedirectOnFeatureSelection(true);
        };
    }, []);

    const saveRouteProfileStop = useCallback(
        async ({
            routeProfileId,
            transitStopId,
            onStopAdditionFn,
        }: {
            routeProfileId: string;
            transitStopId: string;
            onStopAdditionFn: (reloadLayer?: boolean) => Promise<void>;
        }) => {
            const abortController = new AbortController();
            const resp = await publicTransitApi.addRouteStop(
                {
                    routeProfileStop: {
                        publicTransitRouteProfile: {
                            id: routeProfileId,
                        },
                        transitStop: {
                            id: transitStopId,
                        },
                    },
                },
                abortController.signal
            );

            if (isErrorResponse(resp as any)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                addToast(`${t('common.save-successful')}`, 'success');

                //TODO onStopAdditionFn in hook dependencies recreates addStop and doesn't allow to _map?.off('click', stopsLayerId, addStop)
                await onStopAdditionFn(true);
            }
        },
        []
    );

    const addStop = useCallback(
        async (event: mapboxgl.MapLayerMouseEvent) => {
            if (isSavingRef.current) {
                return;
            }

            isSavingRef.current = true;

            const { target, originalEvent } = event;
            originalEvent.stopPropagation();

            const features = target.queryRenderedFeatures(event.point, {
                layers: [stopsLayerId],
            });
            const uniqueFeatures = getUniqueFeatures(
                features as MapboxGeoJSONFeatureWithProperties[]
            );

            if (uniqueFeatures.length > 1) {
                const [feature] = uniqueFeatures;
                const [lng, lat] = getFeatureCenter(feature);
                if (lng && lat) {
                    addToast(
                        t('transit-lines.add-stop-to-route.stop-selection-several-stops-warn'),
                        'warning'
                    );
                    dispatch(flewToPoint({ lng, lat, zoom: 20 }));
                }
                isSavingRef.current = false;

                return;
            }

            for (const feature of uniqueFeatures) {
                const {
                    properties: { id: transitStopId },
                } = feature;
                await saveRouteProfileStop({ routeProfileId, transitStopId, onStopAdditionFn });
            }

            isSavingRef.current = false;
        },
        [stopsLayerId, routeProfileStopEntityName, routeProfileId, saveRouteProfileStop]
    );

    const addStopFromSearch = useCallback(
        async (entity: BaseEntityNameLess) => {
            const { id } = entity;
            if (id) {
                await saveRouteProfileStop({ routeProfileId, transitStopId: id, onStopAdditionFn });
            }
        },
        [routeProfileId, onStopAdditionFn, saveRouteProfileStop]
    );

    const btnClickHandler = useCallback(
        debounce(async () => {
            setSelectionMode(prev => {
                startedSelectionRef.current = !prev;
                return !prev;
            });
        }, 300),
        [setSelectionMode]
    );

    useEffect(() => {
        if (selectionMode) {
            disableLayersInteractivityExceptEntityName(entityName);
            toggleRedirectOnFeatureSelection(false);

            mapboxMapId?.off('click', stopsLayerId, addStop);
            mapboxMapId?.on('click', stopsLayerId, addStop);
        } else if (startedSelectionRef.current) {
            mapboxMapId?.off('click', stopsLayerId, addStop);

            recoverLayersInteractivity();
            toggleRedirectOnFeatureSelection(true);
        }

        return () => {
            mapboxMapId?.off('click', stopsLayerId, addStop);
            if (selectionMode) {
                recoverLayersInteractivity();
            }
        };
    }, [entityName, selectionMode, stopsLayerId, recoverLayersInteractivity]);

    return (
        <div className="add-stop-to-route">
            <div className="controls">
                <Button
                    text={
                        selectionMode
                            ? t('transit-lines.add-stop-to-route.stop-selection-mode-on')
                            : t('transit-lines.add-stop-to-route.stop-selection-mode-off')
                    }
                    onClick={btnClickHandler}
                    colorType="transparent"
                    testId="addStopToRouteBtn"
                    disabled={isSavingRef.current || !layerClickable}
                />
                <Tooltip title={t('transit-lines.add-stop-to-route.find-in-list-tooltip')}>
                    <IconButton
                        icon={AddFromListIcon}
                        width={24}
                        height={24}
                        borderless
                        onClick={handleOpenDialog}
                        disabled={!selectionMode}
                    />
                </Tooltip>
            </div>
            <DialogComponent
                openDialog={openDialog}
                onCloseFn={handleCloseDialog}
                dialogTitle={t('transit-lines.add-stop-to-route.find-in-list-dialog-title')}
            >
                <div style={{ maxHeight: '400px' }}>
                    <TransitStopsList
                        showNewButton={false}
                        hasLink={false}
                        onRowClick={addStopFromSearch}
                    />
                </div>
            </DialogComponent>
            <LockedLayerNotifier
                layerId={stopsLayerId}
                toastMsg={t('transit-lines.add-stop-to-route.stop-selection-with_locked_layer')}
                // showWarning={!selectionMode}
            />
        </div>
    );
});

export default AddStopToRoute;
